import React from 'react';
import {
  Button,
  Icon,
  Dialog,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  Avatar,
  ListItemText,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Pagination,
  type SxProps,
  type Theme
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ClearFilters, Loader, SearchIconButton } from '.';
import { LoaderSize } from '../constants/constants';
import { extractApiErrorMessage, notifications } from '../utils';
import { VeterinaryViewModel } from '../models/vets';
import VetService from '../hooks/VetService';
import { tokens } from '../theme/tokens';
import { V4_DIALOG_MD, V4_OUTLINED_DEFAULT } from '../theme/v4Compat';

interface IProps {
  veterinary: VeterinaryViewModel | null;
  onChange: (veterinary: VeterinaryViewModel | null) => void;
  disableAny?: boolean;
  disabled?: boolean;
  /** Extra styles for the trigger button. */
  sx?: SxProps<Theme>;
  excludeIds?: string[];
}

export interface VeterinarySelectModalHandle {
  openModal: () => void;
}

const VeterinarySelectModal = React.forwardRef<VeterinarySelectModalHandle, IProps>(
  ({ veterinary, onChange, disableAny, sx, disabled, excludeIds }, ref) => {
    const { t } = useTranslation();
    const vetService = new VetService();

    const [veterinaries, setVeterinaries] = React.useState([] as VeterinaryViewModel[]);
    const [isBusy, setIsBusy] = React.useState(false);
    const [loaded, setLoaded] = React.useState(false);
    const [open, setOpen] = React.useState(false);

    const [text, setText] = React.useState('');
    const [searchText, setSearchText] = React.useState('');

    React.useImperativeHandle(ref, () => ({
      openModal() {
        setOpen(true);
      }
    }));

    // pagination
    const [page, setPage] = React.useState(1);
    const [total, setTotal] = React.useState(0);

    const handleChangePage = (_event: unknown, newPage: number) => {
      setPage(newPage);
      setLoaded(false);
    };
    // * pagination

    const displayCount = 5;

    const loadVeterinaries = async () => {
      setIsBusy(true);

      vetService
        .getVeterinaries(page, displayCount, searchText)
        .then((result) => {
          setVeterinaries(result.data);
          setTotal(result.total);
          setLoaded(true);
        })
        .catch((e) => {
          notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
        })
        .finally(() => {
          setIsBusy(false);
        });
    };

    const handleSelectorClick = () => {
      setOpen(true);
    };

    React.useEffect(() => {
      if (open && !loaded) {
        loadVeterinaries();
      }
    }, [open, loaded]);

    React.useEffect(() => {
      if (!veterinary) {
        setTextAny();
      } else {
        setTextByVeterinary(veterinary);
      }
    }, [veterinary]);

    const handleClose = () => {
      setOpen(false);
    };

    const setTextByVeterinary = (veterinary: VeterinaryViewModel) => {
      setText(`${veterinary.number} ${veterinary.name}`);
    };

    const setTextAny = () => {
      setText(t('f-veterinary-any'));
    };

    const handleVeterinarySelectClick = (veterinary: VeterinaryViewModel) => {
      setTextByVeterinary(veterinary);
      setOpen(false);
      onChange(veterinary);
    };

    const handleSelectAny = () => {
      setOpen(false);
      setTextAny();
      onChange(null);
    };

    const clearFilters = () => {
      setSearchText('');
    };

    const renderFiltersSection = () => {
      return (
        <div className="flex flex-col justify-center">
          <div className="box-border flex flex-wrap items-center justify-center pb-[5px] pl-[10px]">
            <div>
              <TextField
                label={t('f-com-search-veterinary')}
                variant="outlined"
                size="small"
                fullWidth
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>

            <div>
              <ClearFilters onClick={clearFilters} />
            </div>

            <div>
              <SearchIconButton onClick={() => setLoaded(false)} />
            </div>
          </div>
        </div>
      );
    };

    return (
      <>
        <Button
          size="medium"
          variant="outlined"
          color="inherit"
          sx={[V4_OUTLINED_DEFAULT, ...(Array.isArray(sx) ? sx : [sx])]}
          disabled={disabled}
          endIcon={<Icon>{'home_work'}</Icon>}
          onClick={handleSelectorClick}
        >
          {text}
        </Button>

        <Dialog
          onClose={handleClose}
          aria-labelledby="veterinary-select-title"
          maxWidth="md"
          sx={V4_DIALOG_MD}
          open={open}
        >
          <DialogTitle id="veterinary-select-title" sx={{ background: tokens.sidebar }}>
            {t('f-veterinary-select-modal')}
          </DialogTitle>

          <DialogContent sx={{ p: 0 }}>
            {renderFiltersSection()}

            <Divider />

            <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

            {loaded && veterinaries && (
              <List dense>
                {veterinaries
                  .filter((veterinary) => !excludeIds || !excludeIds.includes(veterinary.id))
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((veterinary) => (
                    <ListItemButton onClick={() => handleVeterinarySelectClick(veterinary)} key={veterinary.id}>
                      <ListItemAvatar>
                        <Avatar>{veterinary.name[0].toUpperCase() || '-'}</Avatar>
                      </ListItemAvatar>
                      <ListItemText primary={veterinary.name} secondary={veterinary.number} />
                    </ListItemButton>
                  ))}

                <ListItem id="pagination-li" sx={{ justifyContent: 'center' }}>
                  <Pagination
                    count={Math.ceil(total / displayCount)}
                    size="small"
                    color="primary"
                    showFirstButton
                    showLastButton
                    page={page}
                    onChange={handleChangePage}
                  />
                </ListItem>
              </List>
            )}
          </DialogContent>

          <DialogActions sx={{ background: tokens.sidebar }}>
            <Button onClick={handleClose} color="primary">
              {t('m-global-close')}
            </Button>

            <Button onClick={handleSelectAny} color="primary" disabled={disableAny} variant="outlined">
              {t('f-veterinary-any')}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
);

export default VeterinarySelectModal;
